/**
 * Date/time handling for the training session editor.
 *
 * A session's lifecycle status is not stored — the API derives it at read time
 * from `scheduled_at + duration_minutes` against NOW(). That is the right
 * design, but it made one mistake in the form instantly visible and completely
 * baffling: the editor opened with an empty date and a hard-coded 10:00, so an
 * admin who picked today's date any time after 10am saved a session whose slot
 * had already passed, and the list rendered it as **Completed** the moment it
 * was created. Nothing was wrong with the session; it was scheduled into the
 * past and the derivation faithfully reported that.
 *
 * The fix is to never let the form produce a past instant by accident: default
 * to the next whole hour, bound the date input at today, and refuse to create
 * a session whose slot has already gone by.
 */

export interface SessionSlot {
  /** 'YYYY-MM-DD', in the admin's own timezone (what <input type="date"> wants). */
  date: string;
  /** 'HH:MM', 24-hour (what <input type="time"> wants). */
  time: string;
}

const pad = (value: number): string => String(value).padStart(2, '0');

/** Local calendar date of `at` as 'YYYY-MM-DD' — never UTC, which shifts the day. */
export function toLocalDateString(at: Date): string {
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/** Local wall-clock time of `at` as 'HH:MM'. */
export function toLocalTimeString(at: Date): string {
  return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/**
 * The slot a brand-new session opens on: the next whole hour. Always in the
 * future, so the default can never produce an instantly-completed session, and
 * it rolls the date forward on its own when "the next hour" is tomorrow.
 */
export function defaultSessionSlot(now: Date = new Date()): SessionSlot {
  const next = new Date(now.getTime());
  next.setMinutes(0, 0, 0);
  next.setHours(next.getHours() + 1);
  return { date: toLocalDateString(next), time: toLocalTimeString(next) };
}

/**
 * The absolute instant a slot names, or null if it is not a complete, valid
 * date/time. Parsing through the Date constructor with no offset pins it to
 * the admin's timezone, which is what `.toISOString()` on the way out expects.
 */
export function composeSessionInstant(slot: SessionSlot): Date | null {
  if (!slot.date || !slot.time) return null;
  const parsed = new Date(`${slot.date}T${slot.time}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Whether a slot has already gone by. An incomplete slot is not "in the past" —
 * it is simply not filled in yet, and the required-field checks report that.
 */
export function isSessionSlotInPast(slot: SessionSlot, now: Date = new Date()): boolean {
  const instant = composeSessionInstant(slot);
  return instant !== null && instant.getTime() <= now.getTime();
}
